// @file Surface remake link store

import { captureFetchException } from '@@/bits/error_tracker'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import PadletApi from '@@/surface/padlet_api'
import type { WallRemakeLink } from '@@/types'

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export enum RemakeLinkPanelStatus {
  Loading = 'Loading',
  Completed = 'Completed',
  Error = 'Error',
}

export const useSurfaceRemakeLinkStore = defineStore('surfaceRemakeLinkStore', () => {
  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfaceStore = useSurfaceStore()
  const remakeLinkStatus = ref(RemakeLinkPanelStatus.Completed)
  const isRemakeLinkEnabled = ref(false)
  const isRemakeLinkLoading = computed(() => remakeLinkStatus.value === RemakeLinkPanelStatus.Loading)

  // Remake link attributes
  const remakeLinkUrl = ref<string | null>(null)
  const remakeLinkCopyPosts = ref<string | null>(null)
  const remakeLinkCopyPostAuthorship = ref<boolean | null>(null)
  const remakeLinkCopyWallpaper = ref<boolean | null>(null)
  const remakeLinkAddAdmins = ref<boolean | null>(null)
  const remakeLinkLimitOneRemakePerUser = ref<boolean | null>(null)

  async function fetchRemakeLink(): Promise<void> {
    try {
      const attributes = await PadletApi.RemakeLink.fetch(surfaceStore.wallId)
      // If we fetch undefined, it means remake link is not enabled
      if (attributes !== undefined) {
        setRemakeLinkAttributes(attributes)
        isRemakeLinkEnabled.value = true
      } else {
        isRemakeLinkEnabled.value = false
      }
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelRemakeLinkFetch' })
    }
  }

  async function toggleRemakeLink(): Promise<void> {
    try {
      // don't allow changes if mid-change
      if (isRemakeLinkLoading.value) return
      if (isRemakeLinkEnabled.value) {
        // TODO: Use ConfirmationDialog to confirm disabling remake link
        void disableRemakeLink()
      } else {
        // Only show loading state if toggling to True
        remakeLinkStatus.value = RemakeLinkPanelStatus.Loading
        await enableRemakeLink()
        remakeLinkStatus.value = RemakeLinkPanelStatus.Completed
      }
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelRemakeLinkToggle' })
      remakeLinkStatus.value = RemakeLinkPanelStatus.Error
    }
  }

  function setRemakeLinkAttributes(attributes: WallRemakeLink): void {
    remakeLinkUrl.value = attributes.link
    remakeLinkCopyPosts.value = attributes.copyPosts
    remakeLinkCopyPostAuthorship.value = attributes.useOriginalPostsAuthorship
    remakeLinkCopyWallpaper.value = attributes.useOriginalWallpaper
    remakeLinkAddAdmins.value = attributes.addAdminsAsCollaborator
    remakeLinkLimitOneRemakePerUser.value = attributes.limitToOneRemakePerUser
  }

  function resetRemakeLinkAttributes(): void {
    remakeLinkUrl.value = null
    remakeLinkCopyPosts.value = null
    remakeLinkCopyPostAuthorship.value = null
    remakeLinkCopyWallpaper.value = null
    remakeLinkAddAdmins.value = null
    remakeLinkLimitOneRemakePerUser.value = null
  }

  async function enableRemakeLink(): Promise<void> {
    const linkAttributes = await PadletApi.RemakeLink.enable(surfaceStore.wallId)
    setRemakeLinkAttributes(linkAttributes)
    isRemakeLinkEnabled.value = true
  }

  async function disableRemakeLink(): Promise<void> {
    await PadletApi.RemakeLink.disable(surfaceStore.wallId)
    resetRemakeLinkAttributes()
    isRemakeLinkEnabled.value = false
  }

  return {
    fetchRemakeLink,
    isRemakeLinkEnabled,
    isRemakeLinkLoading,
    toggleRemakeLink,
    remakeLinkUrl,
  }
})
